import React, { useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingDomainSetupPanel.scss';

import { setBrandDomainsToSetup } from '../../../Actions/OnboardingActions';
import { updateBrand } from '../../../Actions/BrandActions';
import { getBrand } from '../../../Helpers/user_helpers';
import { isValidDomain } from '../../../Helpers/formatting';
import { brandOnboardingDomainSetupIsComplete } from '../../../Helpers/onboarding_helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';
import Tooltip from '../../General/Tooltip';

const BrandOnboardingDomainSetupPanel = props => {
  const { onboarding, user } = props;
  const brandIsNotOnNetworkAndNotOnShopify = onboarding.usesShopify === false && onboarding.integratingMerchant !== 'shopmy';
  const allBrandDomains = new Set([
    getBrand(user).domain,
    ...(getBrand(user)?.altDomains || '')
      .split(',')
      .map(d => d.trim())
      .filter(d => d)
  ]);
  const getEmptyDomain = domain => ({
    domain: domain || '',
    placeholder: 'domain.com',
    isValid: true,
    errorMessage: 'Please enter a valid domain.'
  });

  const getStartingDomains = () => {
    if (onboarding?.domainsToSetup?.length) return onboarding.domainsToSetup;

    const primaryBrandDomain = getBrand(user)?.domain;
    const altDomains = getBrand(user)?.altDomains || '';
    const allDomains = [
      primaryBrandDomain,
      ...altDomains
        .split(',')
        .map(d => d.trim())
        .filter(d => d)
    ];

    if (allDomains.length === 0) return [getEmptyDomain()];
    return allDomains.map(d => getEmptyDomain(d));
  };

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [domainsToSetup, setDomainsToSetup] = useState(getStartingDomains());
  const allDomainsAreSetup = domainsToSetup.every(d => brandOnboardingDomainSetupIsComplete(onboarding, d));

  const domainIsADuplicate = (domain, index) =>
    domainsToSetup.filter(d => d.domain === domain).length > 1 && domainsToSetup.findIndex(d => d.domain === domain) !== index;

  const validateFields = () => {
    let firstErrorMessage = null;
    props.setIsLoading(true);

    const augmentedDomainsToSetup = domainsToSetup.map((domainToSetup, index) => {
      const isValid = isValidDomain(domainToSetup.domain?.trim()) && !domainIsADuplicate(domainToSetup.domain, index);

      let errorMessage;
      if (domainIsADuplicate(domainToSetup.domain, index)) {
        errorMessage = 'This domain has already been added.';
        firstErrorMessage = firstErrorMessage || errorMessage;
      } else if (!isValid) {
        const { domain } = domainToSetup;
        errorMessage = !domain
          ? 'Please enter a domain.'
          : domain.includes('http')
          ? 'Please enter a domain without "http://" or "https://".'
          : domain.includes('www.')
          ? 'Please enter a domain without "www".'
          : domain.includes('/')
          ? 'Please enter a domain without any slashes ("/").'
          : domain.includes('?')
          ? 'Please enter a domain without any URL parameters (e.g. "?ref=123").'
          : 'Please enter a valid domain.';
        firstErrorMessage = firstErrorMessage || errorMessage;
      }

      return { ...domainToSetup, isValid, errorMessage };
    });

    setDomainsToSetup(augmentedDomainsToSetup);
    props.setIsLoading(false);
    return firstErrorMessage;
  };

  const onNextClick = async domainToSetupNext => {
    if (props.isLoading) return;

    props.setIsLoading(true);
    const firstErrorMessage = validateFields();
    if (firstErrorMessage) {
      props.setIsLoading(false);
      return window.ALERT.error(firstErrorMessage);
    }

    const domains = domainsToSetup.map(domainToSetup => {
      return { ...domainToSetup, domain: domainToSetup.domain.trim().toLowerCase(), setupNext: domainToSetup.domain === domainToSetupNext };
    });
    props.setBrandDomainsToSetup(domains);

    const altDomains = domains
      .map(d => d.domain)
      .filter(d => d && d !== getBrand(user)?.domain)
      .join(',');
    if (getBrand(user).altDomains !== altDomains) {
      const updateBrandResponse = await props.updateBrand(getBrand(user), { altDomains });
      if (updateBrandResponse?.error) {
        props.setIsLoading(false);
        return window.ALERT.error(updateBrandResponse.error);
      }
    }

    if (allDomainsAreSetup) props.goToPanel('Review', 'Invoice');
    else props.goToNextPanel();
  };

  const addDomainField = () => {
    if (props.isLoading) return;
    const newDomainsToSetup = [...domainsToSetup, getEmptyDomain()];
    setDomainsToSetup(newDomainsToSetup);
  };

  const removeDomainField = index => {
    if (props.isLoading) return;
    if (domainsToSetup.length <= 1) return window.ALERT.error('You must have at least one domain set up.');

    const domainToRemove = domainsToSetup[index].domain;
    if (domainToRemove === getBrand(user)?.domain && !domainIsADuplicate(domainToRemove, index))
      return window.ALERT.error('You cannot remove your primary brand domain.');

    const newDomainsToSetup = domainsToSetup.filter((_, i) => i !== index);
    setDomainsToSetup(newDomainsToSetup);
  };

  return (
    <div className='brand-onboarding-domain-setup-panel-outer'>
      <div className='brand-onboarding-domain-setup-panel-inner'>
        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-container-inner'>
                <div className='brand-onboarding-graphic-container-stat'>
                  <div className='brand-onboarding-graphic-container-stat-label'>Our Numbers</div>
                  <div className='brand-onboarding-graphic-container-stat-number'>{props.featuredStat.number}+</div>
                  <div className='brand-onboarding-graphic-container-stat-description'>{props.featuredStat.description}</div>
                </div>

                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>

              <div className='brand-onboarding-graphic-background'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay'></div>
              </div>
            </div>
            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Domain Setup</div>
                <div className='brand-onboarding-subtitle-text'>
                  These are the domains that will be associated with your brand. Any main domain or subdomain you want to use should be added here.
                </div>

                <div className='brand-onboarding-input-label'>Domain(s)</div>
                {domainsToSetup.map((domainToSetup, index) => {
                  const isComplete =
                    brandOnboardingDomainSetupIsComplete(onboarding, domainToSetup) &&
                    allBrandDomains.has(domainToSetup.domain) &&
                    !domainIsADuplicate(domainToSetup.domain, index);

                  return (
                    <div key={index} className='brand-onboarding-input-container split brand-onboarding-domain-setup-panel-input'>
                      <Tooltip
                        message={!domainToSetup.isValid ? domainToSetup.errorMessage : isComplete ? 'This domain is set up' : 'Not yet set up'}
                        outerClassName='brand-onboarding-tooltip'
                      >
                        <input
                          className={cn('brand-onboarding-input', { invalid: !domainToSetup.isValid, filled: !!domainToSetup.value })}
                          type='text'
                          placeholder={domainToSetup.placeholder}
                          value={domainToSetup.domain}
                          onChange={e => {
                            const { value } = e.target;
                            const isValid = domainToSetup.isValid ? true : isValidDomain(value.trim());
                            setDomainsToSetup(prev => updateField(prev, index, { domain: value, isValid }));
                          }}
                          disabled={isComplete}
                        />
                        <FontAwesomeIcon
                          icon={isComplete ? faCheckCircle : faTimes}
                          className='brand-onboarding-input-clear-icon'
                          onClick={() => (isComplete ? null : removeDomainField(index))}
                        />
                      </Tooltip>

                      {!isComplete && (
                        <div className='brand-onboarding-domain-setup-panel-buttons'>
                          <button
                            className='brand-onboarding-domain-setup-panel-setup-domain-button'
                            onClick={() => onNextClick(domainToSetup.domain)}
                          >
                            Setup Domain
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                <button className='brand-onboarding-add-domain-button' onClick={addDomainField}>
                  <span>Add Domain</span>
                  <FontAwesomeIcon icon={faPlus} />
                </button>

                <div className='brand-onboarding-pagination-buttons'>
                  <button className='brand-onboarding-button stretch' onClick={() => props.goToPreviousPanel()}>
                    Back
                  </button>
                  <button className='brand-onboarding-button stretch primary' onClick={() => onNextClick()}>
                    {allDomainsAreSetup || brandIsNotOnNetworkAndNotOnShopify ? 'Next' : 'Setup Next Domain'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingDomainSetupPanel.propTypes = {
  onboarding: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  setBrandDomainsToSetup: PropTypes.func.isRequired,

  goToPreviousPanel: PropTypes.func.isRequired,
  goToNextPanel: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  onboarding: state.onboarding,
  user: state.user
});

export default connect(mapStateToProps, {
  setBrandDomainsToSetup,
  updateBrand
})(withRouter(BrandOnboardingDomainSetupPanel));
